// CabinetSections — blocs de la MAQUETTE (page cabinet + page Notre équipe) (v5.22.0).
// Reproduction fidèle : Cabinet ouvert · highlights · carousel Expérience 01/06 ·
// Notre équipe · bandeau disponibilité · Avis clients · Infos pratiques · Domaines détaillés.
import { useRef, useState } from "react";
import { Briefcase, Building2, Camera, Car, ChevronLeft, ChevronRight, Clock, Home, Mail, MapPin, Phone, Scale, ShieldCheck, Users } from "lucide-react";
import { T, PhotoAvatar, PresenceDot, StatusPill } from "./cabinetTheme";

const HL_ICONS = { briefcase: Briefcase, users: Users, shield: ShieldCheck };
const DOMAIN_META = {
  "Droit immobilier": { Icon: Home, bg: "#FFEDD5", fg: "#EA580C", sub: "Foncier, litiges locatifs, transactions immobilières" },
  "Droit des affaires": { Icon: Building2, bg: "#DBEAFE", fg: "#1D4ED8", sub: "Création d'entreprise, contrats, contentieux" },
  "Droit du travail": { Icon: Briefcase, bg: "#FEF3C7", fg: "#B45309", sub: "Relations employeur-employé, licenciements" },
  "Droit de la famille": { Icon: Users, bg: "#DCFCE7", fg: "#0E8A4F", sub: "Mariage, divorce, succession, tutelle" },
  "Droit pénal": { Icon: Scale, bg: "#FFEDD5", fg: "#EA580C", sub: "Assistance et défense pénale" },
  "Assurances": { Icon: ShieldCheck, bg: "#DCFCE7", fg: "#0E8A4F", sub: "Litiges, sinistres, contrats d'assurance" },
};
const REF_BADGE = {
  "Droit immobilier": { bg: "#F97316", fg: "#fff", Icon: Home },
  "Droit des affaires": { bg: "#92400E", fg: "#fff", Icon: Briefcase },
  "Droit pénal": { bg: "#C2410C", fg: "#fff", Icon: Scale },
  "Droit du travail": { bg: "#B45309", fg: "#fff", Icon: Briefcase },
  "Droit de la famille": { bg: "#0E8A4F", fg: "#fff", Icon: Users },
  "Assurances": { bg: "#065F46", fg: "#fff", Icon: ShieldCheck },
};

const SectionHead = ({ title, sub, action, onAction, testid }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
    <div>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: "#111827", letterSpacing: -0.2 }}>{title}</h2>
      {sub && <div style={{ ...T.sub, fontSize: 12, marginTop: 1 }}>{sub}</div>}
    </div>
    {action && (
      <button onClick={onAction} data-testid={testid}
        style={{ background: "none", border: "none", color: "#0E8A4F", fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap", padding: "2px 0" }}>
        {action} →
      </button>
    )}
  </div>
);

// Pile d'avatars qui se chevauchent + pastille "+N".
const AvatarStack = ({ avatars = [], others = 0, size = 32 }) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    {avatars.map((a, i) => (
      <span key={i} style={{ marginLeft: i ? -9 : 0, position: "relative", display: "inline-flex" }}>
        <PhotoAvatar member={a} size={size} style={{ border: "2px solid #fff" }} />
        {i === avatars.length - 1 && <PresenceDot size={8} style={{ position: "absolute", bottom: 0, right: 0, border: "1.5px solid #fff" }} />}
      </span>
    ))}
    {others > 0 && (
      <span style={{ marginLeft: -9, width: size, height: size, borderRadius: "50%", background: "#F3F4F6", border: "2px solid #fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.32, fontWeight: 800, color: "#374151" }}>+{others}</span>
    )}
  </div>
);

// ── « Cabinet ouvert · N interlocuteurs disponibles » (en-tête page cabinet) ─
export const OpenCard = ({ availability, onClick }) => (
  <button onClick={onClick} data-testid="cabinet-open-card"
    style={{ textAlign: "left", border: "1px solid rgba(14,138,79,0.22)", background: "#F0FDF4", borderRadius: 14, padding: "10px 12px", cursor: "pointer", width: 178, flexShrink: 0 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <PresenceDot />
      <span style={{ fontSize: 12.5, fontWeight: 900, color: "#0E8A4F" }}>Cabinet ouvert</span>
      <span style={{ marginLeft: "auto", color: "#9CA3AF", fontSize: 13 }}>›</span>
    </div>
    <div style={{ fontSize: 11, color: "#374151", margin: "4px 0 8px", lineHeight: 1.35 }} data-testid="cabinet-available-count">
      {availability.available_now} interlocuteur{availability.available_now > 1 ? "s" : ""} disponibles maintenant
    </div>
    <AvatarStack avatars={availability.avatars} others={availability.others} size={30} />
  </button>
);

// ── Highlights « Notre cabinet » (10+ années · 500+ clients · taux élevé) ────
export const Highlights = ({ highlights = [] }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(1, highlights.length)}, minmax(0, 1fr))`, gap: 8, minWidth: 0 }} data-testid="cabinet-highlights">
    {highlights.map((h, i) => {
      const Ic = HL_ICONS[h.icon] || Briefcase;
      return (
        <div key={i} style={{ ...T.card, padding: "10px 11px", display: "flex", gap: 9, alignItems: "center" }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: "#FFEDD5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Ic size={15} color="#EA580C" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 900, lineHeight: 1.1 }}>{h.value}</div>
            <div style={{ ...T.sub, fontSize: 10.5, marginTop: 1 }}>{h.label}</div>
          </div>
        </div>
      );
    })}
  </div>
);

// ── Carousel « L'expérience du Cabinet » — 01/06, flèches, points, tactile ──
export const ExperienceCarousel = ({ cabinetName, references = [] }) => {
  const scRef = useRef(null);
  const [idx, setIdx] = useState(0);
  if (!references.length) return null;
  const step = () => (scRef.current?.firstChild?.getBoundingClientRect().width || 280) + 10;
  const go = (d) => scRef.current?.scrollBy({ left: d * step(), behavior: "smooth" });
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div data-testid="cabinet-experience" style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 900, color: "#111827" }}>L'expérience du {cabinetName}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: "#374151" }} data-testid="experience-counter">{pad(idx + 1)} / {pad(references.length)}</span>
          <button onClick={() => go(-1)} style={{ ...T.back, width: 28, height: 28 }} aria-label="Précédent" data-testid="experience-prev"><ChevronLeft size={14} /></button>
          <button onClick={() => go(1)} style={{ ...T.back, width: 28, height: 28 }} aria-label="Suivant" data-testid="experience-next"><ChevronRight size={14} /></button>
        </div>
      </div>
      <div ref={scRef} onScroll={(e) => setIdx(Math.min(references.length - 1, Math.max(0, Math.round(e.currentTarget.scrollLeft / step()))))}
        style={{ display: "flex", gap: 10, overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none", margin: "11px -2px 0", padding: "0 2px" }}>
        {references.map((r) => {
          const b = REF_BADGE[r.domain] || { bg: "#065F46", fg: "#fff", Icon: Scale };
          return (
            <div key={r.reference_id} style={{ flex: "0 0 76%", maxWidth: 290, scrollSnapAlign: "start", border: "1px solid #E5E7EB", borderRadius: 14, overflow: "hidden", background: "#fff" }}
              data-testid={`experience-card-${r.reference_id}`}>
              <div style={{ position: "relative" }}>
                {r.image
                  ? <img src={r.image} alt={r.domain} style={{ width: "100%", height: 108, objectFit: "cover", display: "block" }} loading="lazy" />
                  : <div style={{ height: 108, background: "#F3F4F6" }} />}
                <span style={{ position: "absolute", left: 9, bottom: 8, display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 999, background: b.bg, color: b.fg, fontSize: 10, fontWeight: 800 }}>
                  <b.Icon size={10} /> {r.domain}
                </span>
                {r.is_demo && <span style={{ ...T.badge, ...T.badgeDemo, position: "absolute", top: 8, right: 8 }}>DEMO</span>}
              </div>
              <div style={{ padding: "10px 12px 12px" }}>
                <div style={{ fontSize: 13.5, fontWeight: 900, lineHeight: 1.25 }}>{r.title}</div>
                <p style={{ ...T.sub, fontSize: 11.5, lineHeight: 1.45, margin: "4px 0 8px" }}>{r.description}</p>
                <span style={{ color: "#0E8A4F", fontSize: 11.5, fontWeight: 800 }}>Voir la référence →</span>
              </div>
            </div>
          );
        })}
      </div>
      <Dots count={references.length} idx={idx} />
    </div>
  );
};

const Dots = ({ count, idx }) => (
  <div style={{ display: "flex", justifyContent: "center", gap: 5, marginTop: 9 }}>
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} style={{ width: i === idx ? 14 : 6, height: 6, borderRadius: 999, background: i === idx ? "#0E8A4F" : "#D1D5DB", transition: "width .25s, background-color .25s" }} />
    ))}
  </div>
);

// ── « Notre équipe » (maquette page équipe) ──────────────────────────────────
export const TeamSection = ({ members = [], onProfile }) => (
  <div data-testid="cabinet-team-section" style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
    <SectionHead title="Notre équipe" sub="Des professionnels à votre écoute" action="Voir toute l'équipe" testid="team-see-all" onAction={() => {}} />
    <div style={{ display: "flex", gap: 10, overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none", margin: "11px -2px 0", padding: "0 2px" }}>
      {members.map((m) => (
        <div key={m.member_id} style={{ flex: "0 0 46%", maxWidth: 175, minWidth: 148, scrollSnapAlign: "start", border: "1px solid #E5E7EB", borderRadius: 14, background: "#fff", overflow: "hidden" }}
          data-testid={`team-card-${m.member_id}`}>
          <div style={{ position: "relative" }}>
            {m.photo
              ? <img src={m.photo} alt={m.name} style={{ width: "100%", height: 118, objectFit: "cover", objectPosition: "top", display: "block" }} loading="lazy" />
              : <div style={{ height: 118, background: m.color || "#065F46", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 30, fontWeight: 900 }}>{String(m.name || "?")[0]}</div>}
            <span style={{ position: "absolute", top: 7, left: 7 }}><StatusPill availability={m.availability} /></span>
            {m.availability !== "indisponible" && <PresenceDot size={10} style={{ position: "absolute", bottom: 7, right: 7, border: "2px solid #fff" }} />}
          </div>
          <div style={{ padding: "9px 10px 11px" }}>
            <div style={{ fontSize: 12.5, fontWeight: 900, lineHeight: 1.2 }}>{m.name} {m.is_demo && <span style={{ ...T.badge, ...T.badgeDemo, padding: "1px 6px", fontSize: 8.5 }}>DEMO</span>}</div>
            <div style={{ ...T.sub, fontSize: 10.5, margin: "2px 0 6px" }}>{m.title}</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
              {(m.specialties || []).slice(0, 2).map((s) => (
                <span key={s} style={{ padding: "2px 7px", borderRadius: 999, background: "#F3F4F6", color: "#374151", fontSize: 9.5, fontWeight: 700, whiteSpace: "nowrap" }}>{s}</span>
              ))}
            </div>
            <button onClick={() => onProfile?.(m)} data-testid={`team-profile-${m.member_id}`}
              style={{ width: "100%", padding: "7px 0", borderRadius: 9, border: "1.2px solid #0E8A4F", background: "#fff", color: "#0E8A4F", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
              Voir profil →
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Bandeau vert « N interlocuteurs disponibles maintenant » ────────────────
export const AvailableBanner = ({ availability }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 11, background: "#E8F8EF", border: "1px solid rgba(14,138,79,0.18)", borderRadius: 14, padding: "12px 14px" }} data-testid="cabinet-available-banner">
    <Users size={20} color="#0E8A4F" style={{ flexShrink: 0 }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, color: "#0E8A4F" }}>{availability.available_now} interlocuteur{availability.available_now > 1 ? "s" : ""} disponibles maintenant</div>
      <div style={{ ...T.sub, fontSize: 11 }}>Le cabinet est ouvert et prêt à vous accompagner.</div>
    </div>
    <AvatarStack avatars={availability.avatars} others={availability.others} size={34} />
  </div>
);

// ── « Avis de nos clients » — carousel dynamique (DEMO identifiés) ───────────
export const ReviewsCarousel = ({ reviews = [] }) => {
  const scRef = useRef(null);
  const [idx, setIdx] = useState(0);
  if (!reviews.length) return null;
  const step = () => (scRef.current?.firstChild?.getBoundingClientRect().width || 300) + 10;
  return (
    <div data-testid="cabinet-reviews-section" style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
      <SectionHead title="Avis de nos clients" action="Voir tous les avis" testid="reviews-see-all" onAction={() => {}} />
      <div ref={scRef} onScroll={(e) => setIdx(Math.min(reviews.length - 1, Math.max(0, Math.round(e.currentTarget.scrollLeft / step()))))}
        style={{ display: "flex", gap: 10, overflowX: "auto", scrollSnapType: "x mandatory", scrollbarWidth: "none", margin: "11px -2px 0", padding: "0 2px" }}>
        {reviews.map((rv) => (
          <div key={rv.review_id} style={{ flex: "0 0 84%", maxWidth: 330, scrollSnapAlign: "start", border: "1px solid #E5E7EB", borderRadius: 14, background: "#fff", padding: "12px 13px" }}
            data-testid={`review-card-${rv.review_id}`}>
            <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: rv.color || "#065F46", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, flexShrink: 0 }}>
                {String(rv.author || "?")[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 900 }}>{rv.author} {rv.is_demo && <span style={{ ...T.badge, ...T.badgeDemo, padding: "1px 6px", fontSize: 8.5 }}>DEMO</span>}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#F59E0B", fontSize: 11, letterSpacing: 1 }}>{"★".repeat(rv.rating)}{"☆".repeat(5 - rv.rating)}</span>
                  <span style={{ ...T.sub, fontSize: 10.5 }}>{rv.when_label}</span>
                </div>
              </div>
            </div>
            <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, margin: "8px 0 0" }}>{rv.text}</p>
          </div>
        ))}
      </div>
      <Dots count={reviews.length} idx={idx} />
    </div>
  );
};

// ── « Informations pratiques » : coordonnées · carte + itinéraire · photos ──
export const InfosPratiques = ({ cab, isDesktop, onOpenGallery }) => {
  const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${cab.address || ""} ${cab.city || ""}`)}`;
  const photos = cab.photos?.length ? cab.photos : [cab.cover_photo].filter(Boolean);
  return (
    <div data-testid="cabinet-infos-section">
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: "#111827" }}>Informations pratiques</h2>
      <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "1.2fr 1fr 1fr" : "1fr", gap: 10, marginTop: 11 }}>
        <div style={{ ...T.card, display: "grid", gap: 11, padding: "13px 14px" }}>
          {[[MapPin, <span key="a">{cab.commune}, {cab.city}<br /><span style={T.sub}>Côte d'Ivoire</span></span>],
            ...(cab.phone ? [[Phone, cab.phone]] : []),
            ...(cab.email ? [[Mail, cab.email]] : []),
            [Clock, cab.hours]].map(([Ic, content], i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Ic size={15} color="#0E8A4F" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12.5, color: "#111827", fontWeight: 600, lineHeight: 1.4 }}>{content}</div>
            </div>
          ))}
        </div>
        <div style={{ borderRadius: 14, border: "1px solid #E5E7EB", background: "#EDF5EF", minHeight: 128, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <MapPin size={30} color="#0E8A4F" fill="#0E8A4F" style={{ opacity: 0.9 }} />
          <a href={dirUrl} target="_blank" rel="noreferrer" data-testid="cabinet-directions-btn"
            style={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 999, background: "#fff", border: "1px solid #E5E7EB", color: "#0E8A4F", fontSize: 11.5, fontWeight: 800, textDecoration: "none", whiteSpace: "nowrap", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
            <Car size={13} /> Itinéraire
          </a>
        </div>
        {photos.length > 0 && (
          <div onClick={onOpenGallery} role="button" data-testid="infos-photos-card"
            style={{ borderRadius: 14, overflow: "hidden", position: "relative", minHeight: 128, cursor: onOpenGallery ? "pointer" : "default" }}>
            <img src={photos[0]} alt={cab.name} style={{ width: "100%", height: "100%", minHeight: 128, objectFit: "cover", display: "block" }} loading="lazy" />
            <span style={{ position: "absolute", bottom: 9, right: 9, display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 999, background: "rgba(17,24,39,0.72)", color: "#fff", fontSize: 10.5, fontWeight: 700 }}>
              <Camera size={11} /> Voir les photos <span style={{ opacity: 0.75 }}>1/{photos.length}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── « Domaines d'expertise détaillés » ───────────────────────────────────────
export const DomainesDetail = ({ domains = [], isDesktop }) => (
  <div data-testid="cabinet-domains-detail">
    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: "#111827" }}>Domaines d'expertise détaillés</h2>
    <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr" : "1fr", gap: 8, marginTop: 11 }}>
      {domains.map((d) => {
        const meta = DOMAIN_META[d] || { Icon: Scale, bg: "#F3F4F6", fg: "#374151", sub: "" };
        return (
          <div key={d} style={{ ...T.card, display: "flex", gap: 11, alignItems: "center", padding: "11px 13px" }} data-testid={`domain-detail-${d}`}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: meta.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <meta.Icon size={17} color={meta.fg} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 900 }}>{d}</div>
              <div style={{ ...T.sub, fontSize: 11 }}>{meta.sub}</div>
            </div>
            <span style={{ color: "#9CA3AF", fontSize: 15 }}>›</span>
          </div>
        );
      })}
    </div>
  </div>
);
